/**
 * src/lib/voice/pcmPlayer.js — gap-free streaming PCM playback (TICKET-WISPR-TTS-STREAM)
 *
 * Plays a stream of raw PCM bytes (16-bit signed little-endian, mono) through
 * Web Audio as the bytes arrive, instead of buffering + decoding a complete
 * compressed file. This is the client half of the Wispr-Flow-style TTS
 * latency fix: the server already streams TTS audio chunked; this module lets
 * playback start on the FIRST chunk (~50ms of audio) rather than after the
 * full sentence has downloaded and decoded.
 *
 * Why raw PCM and not MediaSource/MP3:
 *   - decodeAudioData needs the COMPLETE encoded buffer — cannot stream.
 *   - MediaSource for audio/mpeg is unreliable on iOS Safari (ManagedMediaSource
 *     only, iOS 17.1+) and would replace the existing AudioContext graph that
 *     the barge-in VAD (isSourceActive) and iOS gesture-unlock flow depend on.
 *   - Scheduling raw PCM through AudioBufferSourceNodes is the standard
 *     low-latency voice-agent pattern: works everywhere Web Audio works,
 *     including iOS Safari, with sample-accurate gap-free seams.
 *
 * Scheduling model: one cursor (`nextStartTime`). Each converted chunk becomes
 * an AudioBuffer scheduled back-to-back at the cursor; the cursor advances by
 * the chunk's duration. If the network underruns (cursor fell behind
 * ctx.currentTime), we re-anchor slightly ahead — a brief gap, never overlap.
 *
 * Usage (one player per TTS sentence/seq):
 *   const p = createPcmPlayer({ ctx, sampleRate: 24000, onStarted, onEnded });
 *   p.append(uint8Chunk);  // as fetch body chunks arrive
 *   p.end();               // stream complete — flush remainder, fire onEnded
 *                          //   after the last scheduled sample plays out
 *   p.stop();              // barge-in — silence everything now
 *
 * TICKET-623 — getEnvelope(): every scheduled chunk also records its RMS with
 * a wall-clock (performance.now()-based) timestamp in a short ring buffer.
 * This is the "known reference" the barge-in echo guard (echoCorrelation.js)
 * correlates the mic against, instead of capturing speaker output via a
 * separate loopback node — we already have the exact samples in memory.
 */

// 16-bit mono frames. 50ms @ 24kHz — schedule in chunks no smaller than this
// so we don't create hundreds of sub-millisecond AudioBuffers on a drippy
// network stream. The remainder is flushed on end().
const BYTES_PER_SAMPLE = 2;
const MIN_SCHEDULE_SAMPLES = 1200;
// First-chunk anchor: schedule slightly ahead of "now" so the first buffer
// doesn't start in the past (which would clip its head).
const LEAD_SECONDS = 0.03;

/**
 * Convert Int16LE PCM bytes to Float32 samples in [-1, 1).
 * `bytes.length` must be even — callers hold back a trailing odd byte
 * (see createPcmPlayer's carry logic).
 */
export function int16leToFloat32(bytes) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const out = new Float32Array(bytes.byteLength / BYTES_PER_SAMPLE);
  for (let i = 0; i < out.length; i++) {
    out[i] = view.getInt16(i * BYTES_PER_SAMPLE, true) / 32768;
  }
  return out;
}

/**
 * @param {object} opts
 * @param {AudioContext} opts.ctx        unlocked AudioContext (gesture rules apply upstream)
 * @param {number} [opts.sampleRate]     PCM sample rate (OpenAI TTS pcm = 24000)
 * @param {() => void} [opts.onStarted]  first audible sample scheduled (maps to onFirstAudio)
 * @param {() => void} [opts.onEnded]    last scheduled sample finished playing (not fired after stop())
 */
const ENVELOPE_RING_MS = 500; // keep the last ~500ms of reference envelope history

export function createPcmPlayer({ ctx, sampleRate = 24000, onStarted, onEnded, now } = {}) {
  let carry = null;            // Uint8Array holding a trailing odd byte between appends
  let pending = [];            // Float32Array[] accumulated below MIN_SCHEDULE_SAMPLES
  let pendingSamples = 0;
  let nextStartTime = 0;       // schedule cursor in ctx time
  let scheduledSamples = 0;    // total samples handed to the graph
  let liveSources = new Set(); // scheduled-but-not-ended AudioBufferSourceNodes
  let started = false;
  let finished = false;        // end() called
  let stopped = false;         // stop() called — suppress onEnded
  let endedFired = false;
  let envelope = [];           // TICKET-623: [{rms, t}] wall-clock ring buffer
  const clock = now || (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));

  const fireEndedIfDone = () => {
    if (stopped || endedFired || !finished) return;
    if (liveSources.size === 0 && pendingSamples === 0) {
      endedFired = true;
      if (typeof onEnded === 'function') onEnded();
    }
  };

  const scheduleSamples = (samples) => {
    if (stopped || !ctx || samples.length === 0) return;
    const buffer = ctx.createBuffer(1, samples.length, sampleRate);
    // copyToChannel is on every real AudioBuffer; fall back for minimal mocks.
    if (typeof buffer.copyToChannel === 'function') buffer.copyToChannel(samples, 0);
    else if (typeof buffer.getChannelData === 'function') buffer.getChannelData(0).set(samples);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    const now = ctx.currentTime;
    // Underrun (or first chunk): anchor just ahead of now; otherwise butt-join.
    if (nextStartTime < now + 0.001) nextStartTime = now + LEAD_SECONDS;
    source.onended = () => {
      liveSources.delete(source);
      fireEndedIfDone();
    };
    liveSources.add(source);
    source.start(nextStartTime);
    nextStartTime += samples.length / sampleRate;
    scheduledSamples += samples.length;
    // TICKET-623: record this chunk's RMS as a reference-envelope sample,
    // timestamped now (wall-clock) rather than at ctx.currentTime — the
    // barge-in VAD ticks on performance.now(), a different clock origin
    // than the TTS AudioContext, so this keeps both sides comparable.
    // Precision doesn't need to be sample-accurate: echoCorrelation's lag
    // search already covers ±200ms.
    let sumSq = 0;
    for (let i = 0; i < samples.length; i++) sumSq += samples[i] * samples[i];
    const chunkRms = Math.sqrt(sumSq / samples.length);
    const nowMs = clock();
    envelope.push({ rms: chunkRms, t: nowMs / 1000 });
    envelope = envelope.filter((e) => nowMs / 1000 - e.t <= ENVELOPE_RING_MS / 1000);
    if (!started) {
      started = true;
      if (typeof onStarted === 'function') onStarted();
    }
  };

  const flushPending = () => {
    if (pendingSamples === 0) return;
    const merged = new Float32Array(pendingSamples);
    let off = 0;
    for (const arr of pending) { merged.set(arr, off); off += arr.length; }
    pending = [];
    pendingSamples = 0;
    scheduleSamples(merged);
  };

  return {
    /** Feed raw Int16LE bytes as they arrive off the network. */
    append(chunk) {
      if (stopped || finished || !chunk || chunk.byteLength === 0) return;
      let bytes = chunk instanceof Uint8Array ? chunk : new Uint8Array(chunk);
      if (carry) {
        const joined = new Uint8Array(carry.length + bytes.length);
        joined.set(carry, 0);
        joined.set(bytes, carry.length);
        bytes = joined;
        carry = null;
      }
      if (bytes.length % BYTES_PER_SAMPLE !== 0) {
        carry = bytes.slice(bytes.length - 1);
        bytes = bytes.subarray(0, bytes.length - 1);
      }
      if (bytes.length === 0) return;
      const samples = int16leToFloat32(bytes);
      pending.push(samples);
      pendingSamples += samples.length;
      if (pendingSamples >= MIN_SCHEDULE_SAMPLES) flushPending();
    },

    /** No more bytes coming — flush the sub-threshold remainder. */
    end() {
      if (stopped || finished) return;
      finished = true;
      carry = null; // a dangling odd byte at stream end is line noise, drop it
      flushPending();
      fireEndedIfDone(); // empty stream: fire immediately
    },

    /** Barge-in / teardown: silence everything now. onEnded will NOT fire. */
    stop() {
      stopped = true;
      pending = [];
      pendingSamples = 0;
      carry = null;
      for (const source of liveSources) {
        try { source.stop(); } catch { /* spine-emit-ok: already-stopped source throws InvalidStateError, benign */ }
      }
      liveSources.clear();
    },

    /** Seconds of audio handed to the audio graph so far (grows while streaming). */
    scheduledSeconds() {
      return scheduledSamples / sampleRate;
    },

    /** True while any scheduled source hasn't finished playing. */
    isActive() {
      return liveSources.size > 0;
    },

    /** TICKET-623: recent {rms,t}[] of scheduled chunks (wall-clock seconds). */
    getEnvelope() {
      return envelope;
    },
  };
}
